import { useState, useEffect } from "react";
import { Plus, Filter, Search } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { TaskCreationModal } from "@/components/tasks/TaskCreationModal";
import { EnhancedTaskCard } from "@/components/tasks/EnhancedTaskCard";
import { TaskDetailView } from "@/components/tasks/TaskDetailView";
import { TASK_CATEGORIES, TaskCategoryType } from "@/lib/taskCategoryConfig";
import { TaskBoardView } from "@/components/tasks/TaskBoardView";
import { TaskCalendarView } from "@/components/tasks/TaskCalendarView";
import { TaskTimelineView } from "@/components/tasks/TaskTimelineView";
import confetti from "canvas-confetti";
import { useNavigate } from "react-router-dom";
import { 
  sendTaskEmail, 
  getCategoryLabel, 
  formatEmailDate 
} from "@/lib/emailService";
import { toast as sonnerToast } from "sonner";
import { getPhaseTemplateByCategory } from "@/lib/phaseTemplates";

interface CustomTask {
  id: string;
  user_id: string;
  una_id: string | null;
  category_type: TaskCategoryType;
  custom_category: string | null;
  task_title: string;
  task_description: string | null;
  status: string;
  due_date: string | null;
  completed_at: string | null;
  checkpoints: any[];
  assignees: any[];
  tags: any[];
  related_items: any;
  attachments: any[];
  comments: any[];
  blocked_by: string | null;
  estimated_hours: number | null;
  recurring_config: any;
  privacy: string;
  created_at: string;
  order_number: number;
  category?: string | null;
  resources?: any;
  generated_from_strategy?: boolean;
  is_from_workflow?: boolean;
  workflow_id?: string | null;
  workflow_completion_percentage?: number;
  is_admin_assigned?: boolean;
  source_meeting_date?: string | null;
  created_by_admin_id?: string | null;
}

const MyTasks = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [tasks, setTasks] = useState<CustomTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreationModalOpen, setIsCreationModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedTask, setSelectedTask] = useState<CustomTask | null>(null);
  const [viewDetailsTask, setViewDetailsTask] = useState<CustomTask | null>(null);
  const [currentView, setCurrentView] = useState<'list' | 'board' | 'calendar' | 'timeline'>(() => {
    return (localStorage.getItem('taskCenterView') as any) || 'list';
  });
  
  // Filters
  const [filterCategory, setFilterCategory] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterAssignee, setFilterAssignee] = useState<string>("all");
  const [filterSource, setFilterSource] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Persist view preference
  useEffect(() => {
    localStorage.setItem('taskCenterView', currentView);
  }, [currentView]);

  useEffect(() => {
    if (user) {
      fetchTasks();
      
      // Real-time subscription
      const tasksChannel = supabase
        .channel('custom-tasks-changes')
        .on('postgres_changes', { 
          event: '*', 
          schema: 'public', 
          table: 'custom_tasks',
          filter: `user_id=eq.${user.id}`
        }, () => {
          fetchTasks();
        })
        .subscribe();

      return () => {
        supabase.removeChannel(tasksChannel);
      };
    }
  }, [user]);

  const fetchTasks = async () => {
    if (!user) return;

    try {
      setLoading(true);
      
      // Get tasks with assignee data - EXCLUDE workflow tasks
      const { data: tasksData, error } = await supabase
        .from('custom_tasks')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_from_workflow', false)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // For each task, load its assignees from task_assignments
      const tasksWithAssignees = await Promise.all(
        (tasksData || []).map(async (task) => {
          const { data: assignments } = await supabase
            .from('task_assignments')
            .select(`
              id,
              status,
              assigned_at,
              accepted_at,
              started_at,
              completed_at,
              una_member:una_members!una_member_id (
                id,
                full_name,
                member_role,
                email,
                phone
              )
            `)
            .eq('task_id', task.id);

          // Transform assignments into assignee format
          const assignees = (assignments || [])
            .filter(a => a.una_member) // Filter out any null una_member joins
            .map(a => ({
              id: a.una_member.id,
              full_name: a.una_member.full_name,
              member_role: a.una_member.member_role,
              assignment_status: a.status,
              assigned_at: a.assigned_at,
              accepted_at: a.accepted_at,
              started_at: a.started_at,
              completed_at: a.completed_at,
              email: a.una_member.email,
              phone: a.una_member.phone,
            }));

          console.log(`Task ${task.id} (${task.task_title}): Found ${assignees.length} assignees`, assignees);

          return {
            ...task,
            assignees,
          };
        })
      );

      console.log('Tasks with assignees loaded:', tasksWithAssignees.map(t => ({ id: t.id, title: t.task_title, assigneeCount: t.assignees?.length || 0 })));
      setTasks(tasksWithAssignees as CustomTask[]);
    } catch (error: any) {
      console.error('Error fetching tasks:', error);
      toast({
        title: "Error",
        description: "Failed to load tasks",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTask = async (taskData: any) => {
    if (!user) return;

    try {
      // Get UNA ID and user profile
      const { data: una } = await supabase
        .from('unas')
        .select('id')
        .eq('treasury_secretary_id', user.id)
        .maybeSingle();

      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name, email')
        .eq('id', user.id)
        .single();

      const newTask = {
        user_id: user.id,
        una_id: una?.id || null,
        category_type: taskData.category_type,
        custom_category: taskData.custom_category || null,
        task_title: taskData.task_title,
        task_description: taskData.task_description || null,
        importance: taskData.importance,
        urgency: taskData.urgency,
        status: 'not_started',
        due_date: taskData.due_date?.toISOString().split('T')[0] || null,
        checkpoints: taskData.checkpoints as any || [],
        assignees: taskData.assignees as any || [],
        tags: taskData.tags as any || [],
        estimated_hours: taskData.estimated_hours || null,
        privacy: taskData.privacy || "all_members",
        related_items: taskData.related_items as any || {},
        recurring_config: taskData.recurring_config as any || {},
        order_number: tasks.length
      };

      const { data: createdTask, error } = await supabase
        .from('custom_tasks')
        .insert(newTask)
        .select()
        .single();

      if (error) throw error;

      // Save custom fields if any
      if (taskData.customFields && Object.keys(taskData.customFields).length > 0) {
        const customFieldEntries = Object.entries(taskData.customFields).map(([key, value]) => ({
          task_id: createdTask.id,
          field_key: key,
          field_label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          field_type: typeof value === 'number' ? 'number' : typeof value === 'string' && value.includes('-') && !isNaN(Date.parse(value)) ? 'date' : 'text',
          field_value: value as any, // Cast to any for Json compatibility
          is_required: false,
        }));

        await supabase
          .from('task_custom_fields')
          .insert(customFieldEntries);
      }

      // Save notification preferences if UNA exists
      if (una?.id && taskData.notifications) {
        await supabase
          .from('notification_preferences')
          .upsert({
            user_id: user.id,
            una_id: una.id,
            email_on_task_assigned: taskData.notifications.notifyAssignees ?? true,
            email_day_before_due: taskData.notifications.reminder ?? true,
            email_on_task_created: true,
          }, {
            onConflict: 'user_id,una_id'
          });
      }

      // CREATE PHASES if user selected standard phases
      if (taskData.usePhases === 'standard' && taskData.category_type && una?.id) {
        const phaseTemplate = getPhaseTemplateByCategory(taskData.category_type);
        
        if (phaseTemplate) {
          const phaseInserts = phaseTemplate.phases.map(phase => ({
            task_id: createdTask.id,
            una_id: una.id,
            phase_number: phase.phase_number,
            phase_name: phase.phase_name,
            phase_description: phase.phase_description,
            order_number: phase.order_number,
            status: phase.phase_number === 1 ? 'in_progress' : 'pending',
            requirements: phase.requirements as any,
            document_requirements: phase.document_requirements as any,
            signature_requirements: phase.signature_requirements as any,
            estimated_duration_days: phase.estimated_duration_days,
          }));

          const { error: phasesError } = await supabase
            .from('task_phases')
            .insert(phaseInserts);

          if (phasesError) {
            console.error('Error creating phases:', phasesError);
          } else {
            toast({
              title: "Phases Created",
              description: `${phaseTemplate.phases.length} workflow phases added to task`,
            });
          }
        }
      }

      toast({
        title: "Task Created",
        description: "Your task has been added to the task center",
      });

      // CREATE TASK ASSIGNMENTS for selected UNA members
      const assignedMemberNames: string[] = []; // Track for creator notification email
      if (una?.id && taskData.assignedMemberIds && taskData.assignedMemberIds.length > 0) {
        const assignmentErrors = [];
        let successfulAssignments = 0;

        for (const memberId of taskData.assignedMemberIds) {
          try {
            // Create assignment record
            const { error: assignError } = await supabase
              .from('task_assignments')
              .insert({
                task_id: createdTask.id,
                una_member_id: memberId,
                assigned_by: user.id,
                status: 'pending',
                assigned_at: new Date().toISOString()
              });

            if (assignError) {
              console.error('Assignment error:', assignError);
              assignmentErrors.push(memberId);
              continue;
            }

            successfulAssignments++;

            // Get member details for email
            const { data: member } = await supabase
              .from('una_members')
              .select('full_name, email, member_role, user_id')
              .eq('id', memberId)
              .single();

            if (member) {
              assignedMemberNames.push(member.full_name); // Track for creator email
              
              if (member.email) {
              // Check notification preferences
              const { data: prefs } = await supabase
                .from('notification_preferences')
                .select('email_on_task_assigned')
                .eq('user_id', member.user_id || '')
                .eq('una_id', una.id)
                .maybeSingle();

              const shouldNotify = prefs?.email_on_task_assigned !== false;

              if (shouldNotify) {
                // Send assignment notification
                await supabase.functions.invoke('send-task-assignment-notification', {
                  body: {
                    taskId: createdTask.id,
                    unaMemberId: memberId,
                    memberEmail: member.email,
                    memberName: member.full_name,
                    memberRole: member.member_role,
                    taskTitle: taskData.task_title,
                    taskDescription: taskData.task_description || '',
                    taskCategory: taskData.category_type || 'other',
                    dueDate: taskData.due_date?.toISOString(),
                    assignedBy: profile?.full_name || 'Secretary Treasurer',
                    unaName: 'Your UNA',
                    baseUrl: window.location.origin,
                  }
                });
              }
            }
          }

            // Log audit action
            await supabase.from('una_audit_log').insert({
              una_id: una.id,
              user_id: user.id,
              action: 'task_assigned',
              resource_type: 'task',
              resource_id: createdTask.id,
              details: {
                task_title: taskData.task_title,
                assigned_to: member?.full_name,
                member_id: memberId,
              }
            });

          } catch (error) {
            console.error('Error processing assignment:', error);
            assignmentErrors.push(memberId);
          }
        }

        // Show assignment results
        if (successfulAssignments > 0) {
          toast({
            title: "Task Assigned",
            description: `Task assigned to ${successfulAssignments} member${successfulAssignments > 1 ? 's' : ''}`,
          });
        }

        if (assignmentErrors.length > 0) {
          toast({
            title: "Assignment Warning",
            description: `Some assignments couldn't be created (${assignmentErrors.length} failed)`,
            variant: "destructive",
          });
        }
      }

      // Legacy assignee email handling (for backward compatibility)
      const assignees = taskData.assignees || [];
      const assigneeEmails: string[] = [];
      
      if (assignees.length > 0 && una?.id && taskData.notifications?.notifyAssignees !== false) {
        for (const assigneeName of assignees) {
        // Get assignee email
        const { data: assignee } = await supabase
          .from('saved_assignees')
          .select('email, assignee_name, user_id')
          .eq('assignee_name', assigneeName)
          .eq('una_id', una.id)
          .maybeSingle();

          if (assignee?.email) {
            assigneeEmails.push(assigneeName);
            
            // Send email to assignee
            const emailResult = await sendTaskEmail({
              type: 'assignment',
              toEmail: assignee.email,
              toName: assignee.assignee_name,
              taskTitle: taskData.task_title,
              taskDescription: taskData.task_description || '',
              category: getCategoryLabel(taskData.category_type),
              dueDate: taskData.due_date ? formatEmailDate(taskData.due_date) : undefined,
              taskId: createdTask.id,
              creatorName: profile?.full_name || 'System',
            });

            if (emailResult.success) {
              sonnerToast.success(`📧 Email sent to ${assigneeName}`);
            } else {
              sonnerToast.warning(`⚠️ Failed to send email to ${assigneeName}`);
            }
          } else {
            sonnerToast.info(`ℹ️ ${assigneeName} has no email - skipping notification`);
          }
        }
      }

      // Send confirmation email to task creator
      if (profile?.email) {
        // Combine member names from new system and legacy assignee names
        const allAssigneeNames = [...assignedMemberNames, ...assigneeEmails];
        
        const creatorEmailResult = await sendTaskEmail({
          type: 'creator_notification',
          toEmail: profile.email,
          toName: profile.full_name || 'User',
          taskTitle: taskData.task_title,
          category: getCategoryLabel(taskData.category_type),
          dueDate: taskData.due_date ? formatEmailDate(taskData.due_date) : undefined,
          taskId: createdTask.id,
          assigneeNames: allAssigneeNames.length > 0 ? allAssigneeNames : ['No assignees'],
          userId: user.id,
          unaId: una?.id,
        });

        if (creatorEmailResult.success) {
          sonnerToast.success('✅ Confirmation email sent to you');
        }
      }

      setIsCreationModalOpen(false);
      await fetchTasks(); // Refresh the task list
    } catch (error: any) {
      console.error('Error creating task:', error);
      toast({
        title: "Error",
        description: "Failed to create task",
        variant: "destructive",
      });
    }
  };

  const handleToggleComplete = async (taskId: string) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    const oldStatus = task.status;
    const newStatus = task.status === 'completed' ? 'not_started' : 'completed';

    try {
      const { error } = await supabase
        .from('custom_tasks')
        .update({
          status: newStatus,
          completed_at: newStatus === 'completed' ? new Date().toISOString() : null,
        })
        .eq('id', taskId);

      if (error) throw error;

      // Notify watchers about status change
      try {
        await supabase.functions.invoke('notify-task-status-change', {
          body: {
            taskId,
            oldStatus,
            newStatus,
            userId: user?.id
          }
        });
      } catch (notifError) {
        console.error('Error notifying watchers:', notifError);
      }

      if (newStatus === 'completed') {
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        });
        toast({
          title: "🎉 Task Completed!",
          description: "Great progress on your UNA operations",
        });
      }
    } catch (error: any) {
      console.error('Error updating task:', error);
      toast({
        title: "Error",
        description: "Failed to update task",
        variant: "destructive",
      });
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    try {
      const { error } = await supabase
        .from('custom_tasks')
        .delete()
        .eq('id', taskId);

      if (error) throw error;

      toast({
        title: "Task Deleted",
        description: "The task has been removed",
      });
    } catch (error: any) {
      console.error('Error deleting task:', error);
      toast({
        title: "Error",
        description: "Failed to delete task",
        variant: "destructive",
      });
    }
  };

  // Calculate task counts by category
  const taskCounts = tasks.reduce((acc, task) => {
    const category = task.category_type || 'other';
    acc[category] = (acc[category] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Helper function to determine if task is completed
  const isTaskCompleted = (task: CustomTask) => {
    if (task.status === 'completed') return true;
    if (task.assignees && task.assignees.length > 0) {
      return task.assignees.every((a: any) => a.assignment_status === 'completed');
    }
    return false;
  };

  // Filter tasks
  const filteredTasks = tasks.filter(task => {
    // Category filter
    if (filterCategory !== 'all' && task.category_type !== filterCategory) return false;
    
    // Status filter - Enhanced to check both task status and assignment completion
    if (filterStatus !== 'all') {
      // Check task status first
      if (task.status === filterStatus) {
        // Direct match
      } else if (filterStatus === 'completed' && task.assignees && task.assignees.length > 0) {
        // For completed filter, also check if all assignees completed
        const allAssigneesCompleted = task.assignees.every((a: any) => a.assignment_status === 'completed');
        if (!allAssigneesCompleted) return false;
      } else {
        return false;
      }
    }
    
    // Assignee filter
    if (filterAssignee !== 'all') {
      if (filterAssignee === 'unassigned') {
        if (task.assignees && task.assignees.length > 0) return false;
      } else {
        // Filter by specific member ID
        if (!task.assignees || !task.assignees.some((a: any) => a.id === filterAssignee)) {
          return false;
        }
      }
    }
    
    // Source filter
    if (filterSource !== 'all') {
      if (filterSource === 'mine' && task.is_admin_assigned) return false;
      if (filterSource === 'advisor' && !task.is_admin_assigned) return false;
    }
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        task.task_title.toLowerCase().includes(query) ||
        task.task_description?.toLowerCase().includes(query) ||
        task.tags?.some((tag: string) => tag.toLowerCase().includes(query))
      );
    }
    
    return true;
  });

  // Helper function to check if task is overdue
  const isOverdue = (task: CustomTask) => {
    if (!task.due_date) return false;
    return new Date(task.due_date) < new Date() && !isTaskCompleted(task);
  };

  // Helper function to get priority order (lower is higher priority)
  const getPriorityOrder = (quadrant: string | null | undefined) => {
    const order: Record<string, number> = {
      'q1_do_first': 1,
      'q2_schedule': 2,
      'q3_delegate': 3,
      'q4_eliminate': 4,
    };
    return order[quadrant || ''] || 5; // Unknown priorities go last
  };

  // Sorting function
  const sortTasks = (tasks: CustomTask[]) => {
    return [...tasks].sort((a, b) => {
      // Sort by most recently created (newest first)
      const aCreated = new Date(a.created_at || 0).getTime();
      const bCreated = new Date(b.created_at || 0).getTime();
      return bCreated - aCreated;
    });
  };

  // Display tasks - always show all tasks (no completed filter)
  const displayTasks = sortTasks(filteredTasks);

  // Calculate stats - Enhanced to consider assignment completion
  const totalTasks = tasks.length;
  const completedTasksCount = tasks.filter(t => {
    if (t.status === 'completed') return true;
    // Also count if all assignees completed
    if (t.assignees && t.assignees.length > 0) {
      return t.assignees.every((a: any) => a.assignment_status === 'completed');
    }
    return false;
  }).length;
  const inProgressTasks = tasks.filter(t => {
    if (t.status === 'in_progress') return true;
    // Also count if any assignee is in progress
    if (t.assignees && t.assignees.length > 0) {
      return t.assignees.some((a: any) => a.assignment_status === 'in_progress');
    }
    return false;
  }).length;
  const overdueTasks = tasks.filter(t => {
    if (!t.due_date) return false;
    const isOverdue = new Date(t.due_date) < new Date();
    // Check if completed (either task status or all assignees)
    const isCompleted = t.status === 'completed' || 
      (t.assignees && t.assignees.length > 0 && t.assignees.every((a: any) => a.assignment_status === 'completed'));
    return isOverdue && !isCompleted;
  }).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* View Switcher and Controls */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            {/* View Switcher - horizontally scrollable on mobile */}
            <div className="overflow-x-auto w-full sm:w-auto -mx-4 px-4 sm:mx-0 sm:px-0">
              <div className="flex items-center gap-1 bg-muted rounded-lg p-1 min-w-max">
                <Button 
                  variant={currentView === 'list' ? 'secondary' : 'ghost'} 
                  size="sm" 
                  className="gap-1 min-h-[40px] whitespace-nowrap"
                  onClick={() => setCurrentView('list')}
                >
                  📋 <span className="hidden sm:inline">List</span>
                </Button>
                <Button 
                  variant={currentView === 'board' ? 'secondary' : 'ghost'} 
                  size="sm" 
                  className="gap-1 min-h-[40px] whitespace-nowrap"
                  onClick={() => setCurrentView('board')}
                >
                  📊 <span className="hidden sm:inline">Board</span>
                </Button>
                <Button 
                  variant={currentView === 'calendar' ? 'secondary' : 'ghost'} 
                  size="sm" 
                  className="gap-1 min-h-[40px] whitespace-nowrap"
                  onClick={() => setCurrentView('calendar')}
                >
                  📅 <span className="hidden sm:inline">Calendar</span>
                </Button>
                <Button 
                  variant={currentView === 'timeline' ? 'secondary' : 'ghost'} 
                  size="sm" 
                  className="gap-1 min-h-[40px] whitespace-nowrap"
                  onClick={() => setCurrentView('timeline')}
                >
                  📈 <span className="hidden sm:inline">Timeline</span>
                </Button>
              </div>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Button variant="outline" onClick={() => navigate('/tasks/templates')} className="flex-1 sm:flex-none min-h-[44px]">
                📄 <span className="hidden sm:inline ml-1">Sample documents</span>
              </Button>
              <Button onClick={() => setIsCreationModalOpen(true)} className="flex-1 sm:flex-none min-h-[44px]">
                <Plus className="h-4 w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Add Task</span>
                <span className="sm:hidden">Add</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="text-2xl font-bold">{totalTasks}</div>
              <p className="text-xs text-muted-foreground">Total Tasks</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="text-2xl font-bold text-blue-600">{inProgressTasks}</div>
              <p className="text-xs text-muted-foreground">In Progress</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="text-2xl font-bold text-green-600">{completedTasksCount}</div>
              <p className="text-xs text-muted-foreground">Completed</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="text-2xl font-bold text-red-600">{overdueTasks}</div>
              <p className="text-xs text-muted-foreground">Overdue</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col gap-4">
              {/* Filters */}
              <div className="flex flex-col md:flex-row gap-4">
                {/* Search */}
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search tasks..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Category Filter */}
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-full md:w-[200px]">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {TASK_CATEGORIES.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Status Filter */}
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-full md:w-[180px]">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="not_started">Not Started</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="blocked">Blocked</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>


              {/* Assignee Filter */}
              <Select value={filterAssignee} onValueChange={setFilterAssignee}>
                <SelectTrigger className="w-full md:w-[200px]">
                  <SelectValue placeholder="Assigned To" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tasks</SelectItem>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {/* Get unique assignees from all tasks */}
                  {Array.from(
                    new Set(
                      tasks.flatMap(t => t.assignees || []).map((a: any) => a.id)
                    )
                  ).map(memberId => {
                    const assignee = tasks
                      .flatMap(t => t.assignees || [])
                      .find((a: any) => a.id === memberId) as any;
                    const taskCount = tasks.filter(t => 
                      t.assignees?.some((a: any) => a.id === memberId)
                    ).length;
                    return assignee ? (
                      <SelectItem key={memberId} value={memberId}>
                        {assignee.full_name} ({taskCount})
                      </SelectItem>
                    ) : null;
                  })}
                </SelectContent>
              </Select>

              {/* Source Filter */}
              <Select value={filterSource} onValueChange={setFilterSource}>
                <SelectTrigger className="w-full md:w-[180px]">
                  <SelectValue placeholder="All Sources" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  <SelectItem value="mine">Created by Me</SelectItem>
                  <SelectItem value="advisor">Advisor Assigned</SelectItem>
                </SelectContent>
              </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tasks Display - Conditionally render based on view */}
        {displayTasks.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <div className="flex flex-col items-center justify-center space-y-4">
                {tasks.length === 0 ? (
                  <>
                    <div className="text-6xl mb-2">📋</div>
                    <div className="space-y-2">
                      <p className="text-xl font-semibold">No Tasks Created Yet</p>
                      <p className="text-muted-foreground max-w-md">
                        Start managing your UNA operations by creating your first task
                      </p>
                    </div>
                    <Button className="mt-4" onClick={() => setIsCreationModalOpen(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Custom Task
                    </Button>
                  </>
                ) : filterSource === 'advisor' && !tasks.some(t => t.is_admin_assigned) ? (
                  <>
                    <div className="text-6xl mb-2">📋</div>
                    <div className="space-y-2">
                      <p className="text-xl font-semibold">No advisor-assigned tasks</p>
                      <p className="text-muted-foreground max-w-md">
                        Tasks created by your Wealthy Steward advisor will appear here. 
                        These are personalized action items based on your strategy sessions.
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => setFilterSource("all")}
                    >
                      View All Tasks
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="text-6xl mb-2">🔍</div>
                    <div className="space-y-2">
                      <p className="text-xl font-semibold">No matching tasks</p>
                      <p className="text-muted-foreground">Try adjusting your filters</p>
                    </div>
                    <Button
                      variant="outline"
                    onClick={() => {
                      setFilterCategory("all");
                      setFilterStatus("all");
                      setFilterAssignee("all");
                      setFilterSource("all");
                      setSearchQuery("");
                    }}
                    >
                      Clear Filters
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {currentView === 'list' && (
              <div className="space-y-6">
                {/* Active Tasks */}
                <div className="space-y-4">
                  {displayTasks.map(task => (
                    <EnhancedTaskCard
                      key={task.id}
                      task={task}
                      onToggleComplete={handleToggleComplete}
                      onViewDetails={(task) => setViewDetailsTask(task)}
                      onEdit={(task) => {
                        setSelectedTask(task);
                        setIsEditMode(true);
                        setIsCreationModalOpen(true);
                      }}
                      onDelete={handleDeleteTask}
                    />
                  ))}
                </div>
              </div>
            )}
            
            {currentView === 'board' && (
              <TaskBoardView 
                tasks={displayTasks}
                onViewDetails={(task) => setViewDetailsTask(task as CustomTask)}
              />
            )}
            
            {currentView === 'calendar' && (
              <TaskCalendarView 
                tasks={displayTasks}
                onViewDetails={(task) => setViewDetailsTask(task as CustomTask)}
              />
            )}
            
            {currentView === 'timeline' && (
              <TaskTimelineView 
                tasks={displayTasks}
                onViewDetails={(task) => setViewDetailsTask(task as CustomTask)}
              />
            )}
          </>
        )}
      </div>

      {/* Task Creation/Edit Modal */}
      <TaskCreationModal
        isOpen={isCreationModalOpen}
        onClose={() => {
          setIsCreationModalOpen(false);
          setIsEditMode(false);
          setSelectedTask(null);
        }}
        onSave={handleCreateTask}
        taskCounts={taskCounts}
        editTask={isEditMode ? selectedTask : undefined}
      />

      {/* Task Detail View */}
      <TaskDetailView
        task={viewDetailsTask}
        isOpen={!!viewDetailsTask}
        onClose={() => setViewDetailsTask(null)}
        onEdit={(task) => {
          setViewDetailsTask(null);
          setSelectedTask(task);
          setIsEditMode(true);
          setIsCreationModalOpen(true);
        }}
        onDelete={handleDeleteTask}
        onUpdateTask={fetchTasks}
      />
      
    </>
  );
};

export default MyTasks;